import React, { useEffect, useState } from 'react';
import { AlertTriangle, IdCard, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

export const ActiveDriverCorrectionDialog = ({ api, device, open, onOpenChange, onResolved }) => {
  const [currentDriver, setCurrentDriver] = useState(null);
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !device?.id) return;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get(`/fleet/devices/${device.id}/active-driver/correction-options`, { params: { search, limit: 50 } });
        if (active) {
          setCurrentDriver(response.data?.current_driver || null);
          setPeople(response.data?.people || []);
        }
      } catch (error) {
        if (active) toast.error(error?.response?.data?.detail || 'Could not load eligible Drivers');
      } finally {
        if (active) setLoading(false);
      }
    }, search ? 250 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [api, device?.id, open, search]);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(null);
    setReason('');
  }, [open]);

  const submit = async () => {
    if (!selected || !reason.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/fleet/devices/${device.id}/active-driver/correct`, {
        person_id: selected.person_id,
        person_type: selected.person_type,
        reason: reason.trim(),
      });
      toast.success(`Driver corrected to ${selected.person_name}`);
      onResolved?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not correct the active Driver');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="active-driver-correction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="active-driver-correction-title"><IdCard className="h-4 w-4" />Correct Driver</DialogTitle>
          <DialogDescription data-testid="active-driver-correction-description">
            Replace {currentDriver?.person_name || device?.active_driver_session?.person_name || 'the current Driver'} for this ongoing trip. The change and reason will be audited.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100" data-testid="active-driver-correction-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><p>BLE cannot replace the corrected Driver afterward. RFID/iButton remains authoritative.</p>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search eligible Drivers" className="pl-8" data-testid="active-driver-correction-search" />
          </div>
          <div className="max-h-52 space-y-1.5 overflow-y-auto" data-testid="active-driver-correction-people">
            {loading ? <div className="flex justify-center py-5" data-testid="active-driver-correction-loading"><Loader2 className="h-5 w-5 animate-spin" /></div> : people.map((person) => (
              <button key={`${person.person_type}-${person.person_id}`} type="button" onClick={() => setSelected(person)} className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${selected?.person_id === person.person_id ? 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-950/50' : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/60'}`} data-testid={`active-driver-correction-person-${person.person_id}`}>
                <span className="font-semibold">{person.person_name}</span><span className="ml-2 text-xs capitalize text-muted-foreground">{person.person_type}</span>
              </button>
            ))}
            {!loading && people.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground" data-testid="active-driver-correction-empty">No eligible Drivers found.</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="active-driver-correction-reason">Correction reason</Label>
            <Textarea id="active-driver-correction-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="How was the correct Driver confirmed?" data-testid="active-driver-correction-reason" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="active-driver-correction-cancel">Cancel</Button>
            <Button onClick={submit} disabled={!selected || !reason.trim() || submitting} data-testid="active-driver-correction-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm correction'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActiveDriverCorrectionDialog;